// Content for the product feature landing pages and the features hub.
// Each page entry is rendered by SeoLandingPage.js. Pages are English-only like
// the rest of the public marketing site.
//
// URL convention — pages always live under the product they describe, so more
// products can be added later without clashing:
//   /products/<product-slug>/features        product features hub
//   /products/<product-slug>/<keyword-page>  e.g. /products/effy-order/gst-billing-software
//   /tools/<tool>                             free tools not tied to one product
// For a new product, add a PRODUCT constant plus its own page list, and include
// that list in SEO_PAGES below.
//
// Adding a page here is not enough on its own. Every public SEO page must also be
// listed in: scripts/prerender.js (ROUTES), vercel.json (rewrites, www host only)
// and public/sitemap.xml, or crawlers will only ever see an empty app shell.
//
// Only describe features that really exist in the app — these pages are what
// Google (and customers) will hold us to.

import { EFFY_FUEL_PRODUCT } from '../products/effyFuelContent';
import { GUIDE_PAGES } from './seoGuidePages';
import { KEYWORD_PAGES } from './seoKeywordPages';
import { PAGE_EXTRAS } from './seoPageExtras';

export const SITE_URL = 'https://www.effyventure.com';
export const SIGNUP_PATH = '/products/order-tracking/signup';

export const EFFY_ORDER_PRODUCT = {
  name: 'Effy Order',
  path: '/products/effy-order',
  featuresPath: '/products/effy-order/features',
};

const EFFY_ORDER_PAGES = [
  {
    path: '/products/effy-order/inventory-management-software',
    navLabel: 'Inventory Management',
    badge: 'Inventory Management Software',
    metaTitle: 'Inventory Management Software for Small Businesses in India | Effy Order',
    metaDescription: 'Inventory management software that tracks stock in real time, sends low stock alerts, prints barcodes and updates stock on every sale and purchase.',
    heading: 'Inventory management software that',
    headingHighlight: 'updates stock for you.',
    subtitle: 'Stop counting stock by hand. Every sale, purchase and return updates your inventory automatically, so you always know what is on the shelf and what to reorder.',
    heroPoints: ['Real-time stock tracking', 'Low stock alerts', 'Barcode generate & print', 'Excel bulk upload', 'Stale stock insights', 'Item-wise order history'],
    image: '/images/InventoryDetail.PNG',
    imageAlt: 'Effy Order inventory item detail with stock levels',
    sections: [
      {
        label: 'Stock tracking',
        title: 'Always know what is in stock',
        description: 'Create each item once with SKU, HSN code, unit, purchase price and selling price. Stock goes down when you sell and up when you purchase — no manual entries.',
        points: [
          'Stock is deducted automatically when an order is created.',
          'Purchase orders add stock when goods are received.',
          'Credit notes for returns put items back into stock.',
          'See every order and purchase order that touched an item.',
        ],
      },
      {
        label: 'Fast updates',
        title: 'Update hundreds of items in minutes',
        description: 'Built for shops with large catalogues. Add or correct stock in bulk instead of editing one item at a time.',
        points: [
          'Bulk upload new items from an Excel sheet.',
          'Bulk stock update and bulk stock set from Excel.',
          'Express quantity update — scan a barcode and change the count.',
          'Search any item instantly by name, SKU or barcode.',
        ],
      },
      {
        label: 'Alerts & insights',
        title: 'Reorder on time, clear slow stock',
        description: 'Get told before you run out, and see which products are sitting unsold so you can plan purchases better.',
        points: [
          'Low stock alerts before items run out.',
          'Stale stock insights show products that are not moving.',
          'Inventory reports for stock value and movement.',
          'Barcode generator to label and print product barcodes.',
        ],
      },
    ],
    faqs: [
      { question: 'What is inventory management software?', answer: 'Inventory management software keeps track of how much of each product you have. Effy Order updates stock automatically on every order, purchase and return, alerts you when stock is low, and shows reports on stock movement.' },
      { question: 'Can I upload my existing items from Excel?', answer: 'Yes. You can bulk upload items from an Excel sheet and also bulk update or set stock quantities from Excel, so you do not need to type your catalogue in one by one.' },
      { question: 'Does Effy Order support barcodes?', answer: 'Yes. You can generate and print barcodes for your items, scan barcodes while billing, and use express quantity update to change stock by scanning.' },
      { question: 'Will I get alerts when stock is low?', answer: 'Yes. Effy Order alerts you when an item is running low so you can restock on time.' },
    ],
    related: ['/products/effy-order/gst-billing-software', '/products/effy-order/purchase-order-software', '/products/effy-order/barcode-billing-software'],
  },
  {
    path: '/products/effy-order/gst-billing-software',
    navLabel: 'GST Billing',
    badge: 'GST Billing Software',
    metaTitle: 'GST Billing Software & Invoice Generator for Small Business | Effy Order',
    metaDescription: 'GST billing software to create invoices with CGST, SGST and IGST, HSN codes and discounts. Print or share on WhatsApp and get GST reports for filing.',
    heading: 'GST billing software for',
    headingHighlight: 'fast, correct invoices.',
    subtitle: 'Create a GST invoice in seconds. Tax is calculated from your HSN-wise tax profile, with CGST + SGST for intra-state sales and IGST for inter-state sales — automatically.',
    heroPoints: ['CGST / SGST / IGST auto split', 'HSN-wise tax profiles', 'Print or share invoices', 'Item & overall discounts', 'GST reports', 'Quick & express billing'],
    image: '/images/Billing.PNG',
    imageAlt: 'GST invoice generated in Effy Order',
    sections: [
      {
        label: 'GST invoices',
        title: 'Professional GST invoices, no manual tax maths',
        description: 'Set your GST rate once per HSN code and every invoice uses it. The right tax split is chosen from the customer’s state.',
        points: [
          'CGST + SGST for intra-state and IGST for inter-state sales.',
          'Item-wise and overall discounts, flat or percentage.',
          'Additional charges like delivery or packing, with tax.',
          'Item-wise warranty details printed on the invoice.',
        ],
      },
      {
        label: 'Fast billing',
        title: 'Bill a customer in under a minute',
        description: 'Choose the billing mode that suits your counter — a full order form, a quick bill, or an express mode made for phones and tablets.',
        points: [
          'Quick and express billing modes for busy counters.',
          'Scan barcodes or search items by name to add them.',
          'POS billing look for a supermarket-style checkout screen.',
          'Record the amount received and payment mode on the spot.',
        ],
      },
      {
        label: 'Payments & compliance',
        title: 'Track dues and stay ready for GST filing',
        description: 'Know who has paid and who has not, and hand your accountant clean GST reports.',
        points: [
          'Pending amount tracking per order and per customer.',
          'Payment-in receipts and customer advance adjustment.',
          'Credit notes and debit notes for returns and corrections.',
          'GST reports and sales reports, exportable for filing.',
        ],
      },
    ],
    faqs: [
      { question: 'Does Effy Order calculate CGST, SGST and IGST automatically?', answer: 'Yes. Tax is taken from your HSN-wise tax profile. Intra-state sales get CGST + SGST and inter-state sales get IGST, based on the customer’s state.' },
      { question: 'Can I share invoices with customers on WhatsApp?', answer: 'Yes. You can print invoices or send invoice details to customers through WhatsApp and email notifications.' },
      { question: 'Can I give discounts on a GST invoice?', answer: 'Yes. You can give item-wise discounts and an overall discount, either as a flat amount or a percentage. Tax is calculated after the discount.' },
      { question: 'Does it support credit notes and debit notes?', answer: 'Yes. You can create credit notes for returns or overcharges and debit notes for purchase returns, and they are linked to your orders and stock.' },
    ],
    related: ['/products/effy-order/delivery-challan-software', '/products/effy-order/quotation-software', '/tools/gst-calculator'],
  },
  {
    path: '/products/effy-order/order-management-software',
    navLabel: 'Order Management',
    badge: 'Order Management Software',
    metaTitle: 'Order Management Software for Retail & Wholesale | Effy Order',
    metaDescription: 'Order management software to create, track and deliver orders. Upload orders from Excel, track payment status and notify customers on WhatsApp and email.',
    heading: 'Order management software to',
    headingHighlight: 'never miss an order.',
    subtitle: 'Take every order from creation to delivery in one place — with order status, payment status, customer history and automatic customer updates.',
    heroPoints: ['Order & payment status', 'Excel bulk order upload', 'WhatsApp & email updates', 'Customer order history', 'Delivery challans', 'Sales dashboard'],
    image: '/images/order3.PNG',
    imageAlt: 'Order items table in Effy Order',
    sections: [
      {
        label: 'Orders',
        title: 'From new order to delivered, step by step',
        description: 'Track each order through Initiated, In Progress and Completed, and its payment through Pending, Partially Paid and Paid.',
        points: [
          'Create single orders or upload thousands from Excel.',
          'Filter orders by status, payment, customer or invoice number.',
          'Delivery dates and notes on every order.',
          'Stock updates automatically as orders are created.',
        ],
      },
      {
        label: 'Customers',
        title: 'Customer history built automatically',
        description: 'New customers are saved the first time you bill them. Next time, just type their phone number.',
        points: [
          'Every customer’s full order history in one place.',
          'Pending balance per customer at a glance.',
          'Automatic WhatsApp and email order notifications.',
          'Customer-wise sales reports to find your best buyers.',
        ],
      },
      {
        label: 'Dashboard',
        title: 'See how the business is doing',
        description: 'A dashboard with daily and monthly sales charts, plus insights on top products and top buyers.',
        points: [
          'Monthly and daily order trends.',
          'Top products and top buyers.',
          'Sales, GST and inventory reports.',
          'Works on desktop, tablet and mobile.',
        ],
      },
    ],
    faqs: [
      { question: 'What is order management software?', answer: 'Order management software helps you record customer orders, track them until delivery, and keep payments and stock in sync. Effy Order does all of this in one place.' },
      { question: 'Can I upload orders in bulk?', answer: 'Yes. You can upload orders from an Excel sheet, and Effy Order shows how many rows succeeded or failed.' },
      { question: 'Can customers get order updates automatically?', answer: 'Yes. Effy Order can send order notifications to customers through WhatsApp and email.' },
      { question: 'Can I use it on my phone?', answer: 'Yes. Effy Order works in any browser on desktop, tablet and mobile, and has an express billing mode designed for touch screens.' },
    ],
    related: ['/products/effy-order/inventory-management-software', '/products/effy-order/gst-billing-software', '/products/effy-order/delivery-challan-software'],
  },
  {
    path: '/products/effy-order/delivery-challan-software',
    navLabel: 'Delivery Challan',
    badge: 'Delivery Challan Software',
    metaTitle: 'Delivery Challan Software – Create & Print Delivery Challans | Effy Order',
    metaDescription: 'Create and print delivery challans from your orders in one click. Keep delivery, invoice and payment status together with Effy Order challan software.',
    heading: 'Delivery challan software to',
    headingHighlight: 'ship with confidence.',
    subtitle: 'Turn any order into a clean, printable delivery challan for your delivery team — no retyping items, quantities or addresses.',
    heroPoints: ['One-click challan from order', 'Print-ready format', 'Linked to invoice', 'Customer address & items', 'Order status tracking', 'Delivery dates'],
    image: '/images/Order1.PNG',
    imageAlt: 'Creating orders and delivery documents in Effy Order',
    sections: [
      {
        label: 'Delivery challan',
        title: 'Print a delivery challan straight from the order',
        description: 'Items, quantities and customer details come from the order, so the challan always matches what was sold.',
        points: [
          'Create a delivery challan from any order.',
          'Print-ready layout for your delivery team.',
          'Keep challans, invoices and estimates together per order.',
          'Browse all delivery challans in the billing section.',
        ],
      },
      {
        label: 'Tracking',
        title: 'Know what has been delivered',
        description: 'Delivery dates and order status show what is pending, in progress and completed.',
        points: [
          'Delivery date on every order.',
          'Order status from Initiated to Completed.',
          'Payment status tracked alongside delivery.',
          'Notify customers on WhatsApp and email.',
        ],
      },
    ],
    faqs: [
      { question: 'What is a delivery challan?', answer: 'A delivery challan is a document that goes with goods when they are delivered, listing the items and quantities. It is often used before or alongside the tax invoice.' },
      { question: 'Can I create a delivery challan from an existing order?', answer: 'Yes. In Effy Order you can open any order and print its delivery challan in one click.' },
      { question: 'Is the delivery challan linked to the invoice?', answer: 'Yes. The challan, invoice and estimate all come from the same order, so they always match.' },
    ],
    related: ['/products/effy-order/gst-billing-software', '/products/effy-order/order-management-software', '/products/effy-order/quotation-software'],
  },
  {
    path: '/products/effy-order/purchase-order-software',
    navLabel: 'Purchase Orders',
    badge: 'Purchase Order Software',
    metaTitle: 'Purchase Order Software – Suppliers, Purchases & Payments | Effy Order',
    metaDescription: 'Create and print purchase orders, manage suppliers, record purchase payments and debit notes, and add purchased stock to inventory automatically.',
    heading: 'Purchase order software that',
    headingHighlight: 'keeps suppliers organised.',
    subtitle: 'Raise purchase orders to your suppliers, track what you owe them, and let received goods flow straight into your inventory.',
    heroPoints: ['Printable purchase orders', 'Supplier management', 'Purchase payments', 'Debit notes', 'Stock auto-updated', 'Supplier-wise history'],
    image: '/images/InventoryUI.PNG',
    imageAlt: 'Inventory and purchase management in Effy Order',
    sections: [
      {
        label: 'Purchases',
        title: 'Purchase orders in a few clicks',
        description: 'Pick a supplier, add items from your inventory and print a professional purchase order.',
        points: [
          'Create and print purchase orders.',
          'Supplier records with full purchase history.',
          'See every purchase order for an item.',
          'Received goods are added to inventory automatically.',
        ],
      },
      {
        label: 'Supplier payments',
        title: 'Know exactly what you owe',
        description: 'Record payments out to suppliers and keep returns in order.',
        points: [
          'Purchase payments with printable receipts.',
          'Debit notes for purchase returns.',
          'Supplier-wise purchase orders and dues.',
          'Purchase billing alongside your sales billing.',
        ],
      },
    ],
    faqs: [
      { question: 'Can I manage suppliers in Effy Order?', answer: 'Yes. You can add suppliers, create purchase orders for them, and see each supplier’s purchase history.' },
      { question: 'Does a purchase order update my stock?', answer: 'Yes. When you mark goods as received, those items are added to your inventory, so stock stays correct without manual edits.' },
      { question: 'Can I record payments to suppliers?', answer: 'Yes. You can record purchase payments and print a receipt for each one.' },
    ],
    related: ['/products/effy-order/inventory-management-software', '/products/effy-order/gst-billing-software', '/products/effy-order/order-management-software'],
  },
  {
    path: '/products/effy-order/quotation-software',
    navLabel: 'Quotations & Estimates',
    badge: 'Quotation & Estimate Software',
    metaTitle: 'Quotation & Estimate Software – Send Quotes, Convert to Orders | Effy Order',
    metaDescription: 'Create quotations and estimated bills, print or share them and convert approved quotes into orders without retyping. Try it free for 30 days.',
    heading: 'Quotation software to',
    headingHighlight: 'win more orders.',
    subtitle: 'Send a clear estimate before the sale. When the customer says yes, turn the quotation into an order — items, prices and discounts already filled in.',
    heroPoints: ['Quotations & estimates', 'Printable estimated bill', 'Discounts & taxes', 'Convert to order', 'Customer history', 'Share with customers'],
    image: '/images/order2.PNG',
    imageAlt: 'Choosing order or quotation type in Effy Order',
    sections: [
      {
        label: 'Quotations',
        title: 'Professional quotes in seconds',
        description: 'Use the same item list, taxes and discounts as your invoices, so the quote matches the final bill.',
        points: [
          'Create quotations with items, discounts and GST.',
          'Print an estimated bill for customer approval.',
          'Keep all quotations in one list.',
          'Convert an approved quotation into an order.',
        ],
      },
    ],
    faqs: [
      { question: 'What is the difference between a quotation and an invoice?', answer: 'A quotation (estimate) is a price offer sent before the sale. An invoice is the bill issued after the sale. In Effy Order you can turn a quotation into an order and then invoice it.' },
      { question: 'Can I print an estimated bill?', answer: 'Yes. Every quotation can be printed as an estimated bill for your customer.' },
    ],
    related: ['/products/effy-order/gst-billing-software', '/products/effy-order/order-management-software', '/products/effy-order/delivery-challan-software'],
  },
  {
    path: '/products/effy-order/barcode-billing-software',
    navLabel: 'Barcode & POS Billing',
    badge: 'Barcode & POS Billing Software',
    metaTitle: 'Barcode Billing & POS Software for Retail Shops | Effy Order',
    metaDescription: 'Barcode billing software for retail shops. Scan to bill, use a POS-style checkout, print barcode labels and update stock live on any device.',
    heading: 'Barcode billing software for',
    headingHighlight: 'a faster counter.',
    subtitle: 'Scan, bill, done. Add items by scanning their barcode, check out on a POS-style screen, and let stock update itself.',
    heroPoints: ['Scan to bill', 'POS billing screen', 'Barcode generator', 'Barcode label printing', 'Camera scanning', 'Live stock updates'],
    image: '/images/Inventory2.PNG',
    imageAlt: 'Barcode print preview in Effy Order',
    sections: [
      {
        label: 'Billing',
        title: 'Scan items straight into the bill',
        description: 'Use a barcode scanner or the device camera. Items are found instantly and added with their price.',
        points: [
          'Barcode scanner and camera scanning supported.',
          'POS billing look for supermarket-style checkout.',
          'Express mode built for phones and tablets.',
          'Voice search to find items by name.',
        ],
      },
      {
        label: 'Barcodes',
        title: 'Generate and print your own barcodes',
        description: 'No barcode on a product? Create one and print labels in bulk.',
        points: [
          'Barcode generator for your items.',
          'Print barcode labels for your shelves.',
          'Add new items by scanning their barcode.',
          'Update stock by scanning with express quantity update.',
        ],
      },
    ],
    faqs: [
      { question: 'Can I bill using a barcode scanner?', answer: 'Yes. Scan an item’s barcode in the billing screen and it is added to the bill automatically. You can also scan with your phone or tablet camera.' },
      { question: 'Can I print barcodes for my products?', answer: 'Yes. Effy Order has a barcode generator and lets you print barcode labels for your items.' },
      { question: 'Does it work on a tablet or phone at the counter?', answer: 'Yes. Express billing mode is designed for touch screens, with large buttons and camera scanning.' },
    ],
    related: ['/products/effy-order/inventory-management-software', '/products/effy-order/gst-billing-software', '/products/effy-order/order-management-software'],
  },
];

// Every feature landing page and guide page across all products, each tagged with its product
// (used for the breadcrumb and footer grouping).
// The original pages get their extra text (guide, section, FAQs, links) from seoPageExtras.js;
// the newer keyword pages from seoKeywordPages.js are already complete.
function addExtras(page) {
  const extras = PAGE_EXTRAS[page.path];
  if (!extras) {
    return page;
  }
  const merged = { ...page };
  if (extras.metaTitle) {
    merged.metaTitle = extras.metaTitle;
  }
  merged.guide = extras.guide;
  merged.sections = [...page.sections, ...extras.extraSections];
  merged.faqs = [...page.faqs, ...extras.extraFaqs];
  merged.related = [...page.related, ...extras.extraRelated];
  return merged;
}

function buildFeaturePages() {
  const pages = [];
  for (const page of [...EFFY_ORDER_PAGES, ...KEYWORD_PAGES]) {
    pages.push({ ...addExtras(page), product: EFFY_ORDER_PRODUCT });
  }
  return pages;
}

const FEATURE_SEO_PAGES = buildFeaturePages();

// Guide pages name their product by id; this turns it into the real product object.
function buildGuidePages() {
  const pages = [];
  for (const page of GUIDE_PAGES) {
    let product = EFFY_ORDER_PRODUCT;
    if (page.productId === 'fuel') {
      product = EFFY_FUEL_PRODUCT;
    }
    pages.push({ ...page, product, isGuide: true });
  }
  return pages;
}

export const SEO_PAGES = [...FEATURE_SEO_PAGES, ...buildGuidePages()];

// Groups shown on the Effy Order features hub. Each item links to its landing page when one exists.
export const FEATURE_GROUPS = [
  {
    title: 'Sales & Billing',
    icon: '🧾',
    items: [
      { name: 'GST invoices', text: 'CGST, SGST and IGST split automatically from HSN-wise tax profiles.', link: '/products/effy-order/gst-billing-software' },
      { name: 'Invoice billing', text: 'Print or share invoices and record payments on the spot.', link: '/products/effy-order/invoice-billing-software' },
      { name: 'Full, quick & express billing', text: 'Pick the billing speed that suits your counter, including a touch-friendly express mode.', link: '/products/effy-order/gst-billing-software' },
      { name: 'POS billing look', text: 'Supermarket-style checkout screen for retail counters.', link: '/products/effy-order/barcode-billing-software' },
      { name: 'Quotations & estimates', text: 'Send estimated bills and convert approved quotes into orders.', link: '/products/effy-order/quotation-software' },
      { name: 'Delivery challans', text: 'Print a delivery challan from any order in one click.', link: '/products/effy-order/delivery-challan-software' },
      { name: 'Discounts & extra charges', text: 'Item-wise and overall discounts, flat or percentage, plus taxed additional charges.' },
      { name: 'Warranty on invoices', text: 'Record item-wise warranty periods and print them on the invoice.' },
    ],
  },
  {
    title: 'Orders & Customers',
    icon: '📦',
    items: [
      { name: 'Order tracking', text: 'Order status and payment status from creation to delivery.', link: '/products/effy-order/order-management-software' },
      { name: 'Excel bulk order upload', text: 'Upload thousands of orders at once from a spreadsheet.', link: '/products/effy-order/order-management-software' },
      { name: 'Customer management', text: 'Customers saved automatically, with full order history and balances.', link: '/products/effy-order/sales-management-software' },
      { name: 'WhatsApp & email notifications', text: 'Keep customers updated about their orders automatically.' },
    ],
  },
  {
    title: 'Payments & Accounts',
    icon: '💳',
    items: [
      { name: 'Payment-in receipts', text: 'Record customer payments and print receipts.' },
      { name: 'Advance adjustment', text: 'Apply customer advances and credit notes to new orders.' },
      { name: 'Credit notes', text: 'Handle item returns and overcharges cleanly.' },
      { name: 'Debit notes', text: 'Record purchase returns to suppliers.' },
      { name: 'Pending dues', text: 'See what every customer still owes.' },
    ],
  },
  {
    title: 'Inventory',
    icon: '📊',
    items: [
      { name: 'Real-time stock', text: 'Stock updates on every sale, purchase and return.', link: '/products/effy-order/stock-management-software' },
      { name: 'Low stock alerts', text: 'Know before items run out.', link: '/products/effy-order/inventory-management-software' },
      { name: 'Bulk upload & bulk stock update', text: 'Add items and update stock from Excel.', link: '/products/effy-order/inventory-management-software' },
      { name: 'Barcode generator & scanning', text: 'Create, print and scan barcodes; update stock by scanning.', link: '/products/effy-order/barcode-billing-software' },
      { name: 'Stale stock insights', text: 'Find products that are not selling.' },
    ],
  },
  {
    title: 'Purchases & Suppliers',
    icon: '🚚',
    items: [
      { name: 'Purchase orders', text: 'Create and print purchase orders for suppliers.', link: '/products/effy-order/purchase-order-software' },
      { name: 'Supplier management', text: 'Supplier records with purchase history.', link: '/products/effy-order/purchase-order-software' },
      { name: 'Purchase payments', text: 'Record payments out and print receipts.', link: '/products/effy-order/purchase-order-software' },
    ],
  },
  {
    title: 'Reports & Insights',
    icon: '📈',
    items: [
      { name: 'Dashboard', text: 'Daily and monthly sales charts at a glance.' },
      { name: 'GST reports', text: 'GST reports ready for your accountant and filing.', link: '/products/effy-order/gst-report-software' },
      { name: 'Sales & inventory reports', text: 'Export sales, customer-wise and inventory reports.', link: '/products/effy-order/sales-report-software' },
      { name: 'Top products & buyers', text: 'See what sells most and who buys most.' },
    ],
  },
  {
    title: 'Works the way you do',
    icon: '📱',
    items: [
      { name: 'Desktop, tablet & mobile', text: 'Runs in any browser, with touch-friendly screens.' },
      { name: 'English & Tamil', text: 'Use the app in English or Tamil.' },
      { name: 'Built-in help assistant', text: 'Get step-by-step answers inside the app.' },
      { name: 'Free GST calculator', text: 'Add or remove GST from any amount.', link: '/tools/gst-calculator' },
      { name: 'Free HSN code finder', text: 'Search HSN codes and listed GST rates by product name.', link: '/tools/hsn-code-finder' },
    ],
  },
];

export function findSeoPage(path) {
  return SEO_PAGES.find((page) => page.path === path);
}
